import { useMemo } from "react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
  Legend,
} from "recharts";
import { t } from "@/shared/lib/th-translations";
import type {
  PerformanceMetric,
  PerformanceProcessBucket,
} from "@/shared/types/production";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  aggregateEmployees,
  computeProcessStats,
  formatMetricDisplay,
  metricDisplayLabel,
} from "@/frontend/lib/production-stats";

interface Props {
  processes: PerformanceProcessBucket[];
  metric: PerformanceMetric;
}

const PALETTE = [
  "#14b8a6",
  "#8b5cf6",
  "#f59e0b",
  "#3b82f6",
  "#ef4444",
  "#10b981",
  "#ec4899",
  "#6366f1",
  "#84cc16",
  "#f97316",
];

interface Point {
  x: number; // z-score
  y: string; // employee name
  processTitle: string;
  rawValue: number;
}

export default function PerformanceDotPlot({ processes, metric }: Props) {
  const aggregates = useMemo(
    () => aggregateEmployees(processes, metric),
    [processes, metric],
  );

  const { seriesByProcess, employeeOrder } = useMemo(() => {
    const statsByProcess = processes.map((p) => computeProcessStats(p, metric));
    const seriesByProcess: Record<string, Point[]> = {};
    for (let i = 0; i < processes.length; i++) {
      const proc = processes[i];
      const stats = statsByProcess[i];
      if (stats.count < 2) continue;
      const points: Point[] = [];
      for (const b of proc.buckets) {
        const z = stats.zScoreByEmployee.get(b.employeeId) ?? 0;
        const v = stats.valueByEmployee.get(b.employeeId) ?? 0;
        points.push({
          x: z,
          y: b.employeeName,
          processTitle: proc.processTitle,
          rawValue: v,
        });
      }
      seriesByProcess[proc.processTitle] = points;
    }
    const employeeOrder = aggregates.map((a) => a.employeeName);
    return { seriesByProcess, employeeOrder };
  }, [processes, metric, aggregates]);

  const chartHeight = Math.max(300, employeeOrder.length * 28 + 80);
  const metricLabel = metricDisplayLabel(metric);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("production.performance.viz.dotPlot.title")}</CardTitle>
        <p className="text-xs text-muted-foreground">
          {t("production.performance.viz.dotPlot.legend")}
        </p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <ScatterChart margin={{ top: 10, right: 30, left: 60, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              type="number"
              dataKey="x"
              name={t("production.performance.viz.dotPlot.xAxisLabel")}
              domain={[-3, 3]}
              ticks={[-3, -2, -1, 0, 1, 2, 3]}
              tick={{ fontSize: 11 }}
              label={{
                value: t("production.performance.viz.dotPlot.xAxisLabel"),
                position: "insideBottom",
                offset: -10,
                fontSize: 11,
              }}
            />
            <YAxis
              type="category"
              dataKey="y"
              name="employee"
              tick={{ fontSize: 11 }}
              width={100}
              allowDuplicatedCategory={false}
              ticks={employeeOrder}
              reversed
            />
            <ReferenceLine x={0} stroke="#9ca3af" strokeDasharray="4 4" />
            <Tooltip
              cursor={{ strokeDasharray: "3 3" }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0].payload as Point;
                const bandKey =
                  p.x <= -1.5
                    ? "zBandCriticallyLow"
                    : p.x <= -0.5
                      ? "zBandLow"
                      : p.x < 0.5
                        ? "zBandNormal"
                        : p.x < 1.5
                          ? "zBandHigh"
                          : "zBandCriticallyHigh";
                return (
                  <div className="rounded-md border bg-background p-2 shadow-sm text-xs">
                    <div className="font-medium">{p.y}</div>
                    <div className="text-muted-foreground">
                      {p.processTitle}
                    </div>
                    <div>
                      {metricLabel}: {formatMetricDisplay(p.rawValue, metric)}
                    </div>
                    <div>
                      {t(`production.performance.viz.heatmap.${bandKey}`)}
                    </div>
                  </div>
                );
              }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {Object.entries(seriesByProcess).map(([processTitle, points], idx) => (
              <Scatter
                key={processTitle}
                name={processTitle}
                data={points}
                fill={PALETTE[idx % PALETTE.length]}
              />
            ))}
          </ScatterChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
